import { tv } from 'tailwind-variants';
import { controlsPhase } from '../../state/controlsOverlay';
import { t } from '../../state/i18n';
import { MouseIcon, type MouseType } from './MouseIcon';

const container = tv({
  base: [
    'fixed top-[1em] right-[1em] z-[50]',
    'pointer-events-none',
    'flex flex-col gap-[0.45em]',
    'p-[0.7em] rounded-[0.6em]',
    'bg-slate-950/40',
    'border border-white/[0.06]',
    'animate-fade-in',
    'text-[clamp(0.85rem,0.75rem+0.5vw,1.15rem)]',
  ],
});

const row = tv({
  base: 'flex items-center gap-[0.6em]',
});

const keyBadge = tv({
  base: [
    'inline-flex items-center justify-center',
    'min-w-[1.8em] px-[0.45em] py-[0.1em]',
    'text-[0.7em] font-mono font-bold leading-tight',
    'bg-slate-800/80 border border-slate-500/40',
    'rounded-[0.25em]',
    'text-slate-200',
    'shadow-[0_1px_0_0_rgba(0,0,0,0.5)]',
  ],
});

const mouseBadge = tv({
  base: [
    'inline-flex items-center gap-[0.35em]',
    'px-[0.45em] py-[0.1em]',
    'text-[0.7em] font-mono font-bold leading-tight',
    'bg-slate-800/80 border border-slate-500/40',
    'rounded-[0.25em]',
    'text-slate-200',
    'shadow-[0_1px_0_0_rgba(0,0,0,0.5)]',
  ],
});

const labelStyle = tv({
  base: 'text-[0.75em] text-slate-400/80 whitespace-nowrap',
});

const KeyRow = ({ keys, text }: { keys: ReadonlyArray<string>; text: string }) => (
  <div class={row()}>
    <span class="inline-flex items-center gap-[0.15em]">
      {keys.map((k) => (
        <span key={k} class={keyBadge()}>
          {k}
        </span>
      ))}
    </span>
    <span class={labelStyle()}>{text}</span>
  </div>
);

const MouseRow = ({ type, badge, text }: { type: MouseType; badge: string; text: string }) => (
  <div class={row()}>
    <span class={mouseBadge()}>
      <MouseIcon type={type} />
      {badge}
    </span>
    <span class={labelStyle()}>{text}</span>
  </div>
);

export const ControlsOverlay = () => {
  if (controlsPhase.value !== 'visible') {
    return null;
  }

  return (
    <div class={container()}>
      <MouseRow type="left" badge="LMB" text={t('ui.controls.leftClick')} />
      <MouseRow type="right" badge="RMB" text={t('ui.controls.rightClick')} />
      <MouseRow type="scroll" badge="Scroll" text={t('ui.controls.scrollWheel')} />
      <MouseRow type="middle" badge="MMB" text={t('ui.controls.middleDrag')} />
      <div class="h-px bg-white/[0.06] my-[0.15em]" />
      <KeyRow keys={['W', 'A', 'S', 'D']} text={t('ui.controls.wasd')} />
      <KeyRow keys={['B']} text={t('ui.controls.buildMenu')} />
      <KeyRow keys={['F']} text={t('ui.controls.fightMode')} />
      <KeyRow keys={['ESC']} text={t('ui.controls.escape')} />
    </div>
  );
};
